import { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    Card, CardContent, CardActions,
    TextField, Autocomplete, Checkbox, FormControlLabel,
    Button, Box, Divider, Typography, IconButton, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Link as MuiLink,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link } from "react-router-dom";
import api from "../../shared/api";
import FlashAlert from "../common/FlashAlert";

export default function LoginForm({ viewModel, flash }) {
    const isDev = viewModel.environment === "development";

    const schema = useMemo(() => z.object({
        client_id: z.string().min(1, "The username field is required.").max(25),
        client_secret: isDev
            ? z.string()
            : z.string().min(1, "The password field is required."),
        arcID: z.number({ required_error: "The program year field is required." }),
        braID: z.number({ required_error: "The branch field is required." }),
        rememberMe: z.boolean(),
    }), [isDev]);

    const { control, handleSubmit, setValue, watch, setError, formState: { errors } } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            client_id: "",
            client_secret: "",
            arcID: null,
            braID: null,
            rememberMe: false,
        },
    });

    const [loading, setLoading] = useState(false);
    const [pwVis, setPwVis] = useState(false);
    const [formError, setFormError] = useState("");
    const [showAgreement, setShowAgreement] = useState(false);

    const arcID = watch("arcID");

    const selectedBranches = useMemo(
        () => (arcID != null ? viewModel.branches[arcID] || [] : []),
        [arcID, viewModel.branches]
    );

    const numProgramYears = viewModel.programYears.length;
    const numBranches = selectedBranches.length;

    const organizationName = (viewModel.agency && viewModel.agency.agTitle) || "your organization";

    // Initialize defaults on mount
    useEffect(() => {
        const defaultIdx = Math.max(0, viewModel.programYears.findIndex((y) => y.agDefault));
        const defaultArc = viewModel.programYears[defaultIdx].arcID;
        const defaultBranches = viewModel.branches[defaultArc] || [];
        const defaultBra = defaultBranches.length > 0 ? defaultBranches[0].braID : null;

        let initialArc = defaultArc;
        let initialBra = defaultBra;
        let initialClientId = "";
        let initialRemember = false;

        // Apply rememberMe data
        if (viewModel.rememberData.client_id && viewModel.rememberData.client_id.length > 0) {
            initialRemember = true;
            initialClientId = viewModel.rememberData.client_id;

            if (viewModel.rememberData.arcID &&
                viewModel.programYears.some((y) => y.arcID === viewModel.rememberData.arcID)) {
                initialArc = viewModel.rememberData.arcID;
            }

            const rememberedBranches = viewModel.branches[initialArc] || [];
            if (viewModel.rememberData.braID &&
                rememberedBranches.some((b) => b.braID === viewModel.rememberData.braID)) {
                initialBra = viewModel.rememberData.braID;
            }
        }

        setValue("arcID", initialArc);
        setValue("braID", initialBra);
        setValue("client_id", initialClientId);
        setValue("rememberMe", initialRemember);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = async (data) => {
        setFormError("");
        setLoading(true);

        try {
            const response = await api.post("/login", data, { responseType: "json" });

            if (response.data.location) {
                window.location.replace(response.data.location);
                return; // Don't reset loading — we're redirecting
            }

            if (!response.data.valid && response.data.errors) {
                response.data.errors.forEach((err) => {
                    if (err.PROPERTY.length === 0) {
                        setFormError((prev) => prev + " " + err.MESSAGE);
                    } else {
                        setError(err.PROPERTY, { message: err.MESSAGE });
                    }
                });
            }

            setValue("client_secret", "");
        } catch {
            setFormError("There was a network error processing this request. Please contact support if this problem persists.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form id="login" onSubmit={handleSubmit(onSubmit)}>
            <Card variant="elevation" elevation={0} square sx={{ border: "none" }}>
                <CardContent sx={{ textAlign: "center", pb: 0 }}>
                    <Typography variant="h5">Sign In</Typography>
                </CardContent>
                <CardContent>
                    <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                        <FlashAlert flash={flash} />

                        <Controller
                            name="client_id"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Username"
                                    autoFocus
                                    fullWidth
                                    inputProps={{ maxLength: 25 }}
                                    error={!!errors.client_id}
                                    helperText={errors.client_id?.message}
                                    sx={{ mb: 3 }}
                                    onKeyDown={(e) => e.key === "Enter" && handleSubmit(onSubmit)()}
                                />
                            )}
                        />

                        <Controller
                            name="client_secret"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Password"
                                    type={pwVis ? "text" : "password"}
                                    fullWidth
                                    error={!!errors.client_secret}
                                    helperText={errors.client_secret?.message}
                                    sx={{ mb: 3 }}
                                    onKeyDown={(e) => e.key === "Enter" && handleSubmit(onSubmit)()}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setPwVis(!pwVis)} edge="end" size="small">
                                                    {pwVis ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />

                        {numProgramYears > 1 && (
                            <Controller
                                name="arcID"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        options={viewModel.programYears}
                                        getOptionLabel={(opt) => String(opt.arcYear || "")}
                                        value={viewModel.programYears.find((y) => y.arcID === field.value) || null}
                                        onChange={(_, newVal) => {
                                            const newArc = newVal ? newVal.arcID : null;
                                            field.onChange(newArc);
                                            // Reset branch when program year changes
                                            if (newArc !== arcID) {
                                                const branches = viewModel.branches[newArc] || [];
                                                setValue("braID", branches.length > 0 ? branches[0].braID : null);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Program Year"
                                                error={!!errors.arcID}
                                                helperText={errors.arcID?.message}
                                                sx={{ mb: 3 }}
                                            />
                                        )}
                                        disableClearable
                                    />
                                )}
                            />
                        )}

                        {numBranches > 1 && (
                            <Controller
                                name="braID"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        options={selectedBranches}
                                        getOptionLabel={(opt) => opt.braName || ""}
                                        value={selectedBranches.find((b) => b.braID === field.value) || null}
                                        onChange={(_, newVal) => field.onChange(newVal ? newVal.braID : null)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Branch"
                                                error={!!errors.braID}
                                                helperText={errors.braID?.message}
                                                sx={{ mb: 3 }}
                                            />
                                        )}
                                        disableClearable
                                    />
                                )}
                            />
                        )}

                        <Controller
                            name="rememberMe"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Checkbox checked={field.value} onChange={field.onChange} />}
                                    label="Remember me"
                                />
                            )}
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                            Not recommended on shared computers
                        </Typography>
                    </Box>

                    <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                        {formError ? (
                            <Typography variant="subtitle1" color="error">{formError}</Typography>
                        ) : (
                            <Typography variant="subtitle1">&nbsp;</Typography>
                        )}
                    </Box>

                    <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled={loading}
                            sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, py: 1.2 }}
                        >
                            {loading ? "Signing in..." : "Sign In"}
                        </Button>
                    </Box>
                </CardContent>

                <CardContent sx={{ pt: 0, pb: 1 }}>
                    <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                        <Typography variant="caption" color="text.secondary">
                            By accessing this system, you acknowledge that you have read and agree to comply with the{" "}
                            <MuiLink href="#" onClick={(e) => { e.preventDefault(); setShowAgreement(true); }}>
                                User Access and Acceptable Use Agreement
                            </MuiLink>.
                        </Typography>
                    </Box>
                </CardContent>

                <Divider />

                <CardContent>
                    <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                        <MuiLink component={Link} to="/login/forgot-password" display="block">Forgot my password</MuiLink>
                        <MuiLink component={Link} to="/login/forgot-username" display="block">Forgot my username</MuiLink>
                    </Box>
                </CardContent>

                <Divider />

                {/* User Agreement Dialog */}
                <Dialog open={showAgreement} onClose={() => setShowAgreement(false)} maxWidth="sm" fullWidth scroll="paper">
                    <DialogTitle>User Access and Acceptable Use Agreement</DialogTitle>
                    <Divider />
                    <DialogContent dividers sx={{ maxHeight: 500 }}>
                        <Typography paragraph>
                            This system is provided by Xenegrade and operated on behalf of {organizationName} (&quot;Licensee&quot;). By accessing or using this system, you acknowledge and agree to comply with the terms outlined in this User Access and Acceptable Use Agreement.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2, mb: 0.5 }}>Authorized Use</Typography>
                        <Typography paragraph>Access to this system is granted only to authorized users who have been approved by the Licensee. Users may access the system solely for legitimate business, educational, or administrative purposes related to the programs and services managed by the Licensee.</Typography>
                        <Typography paragraph>Users must comply with all applicable organizational policies, laws, and regulations governing the use of computer systems and personal data.</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2, mb: 0.5 }}>User Responsibilities</Typography>
                        <Typography paragraph>Users are responsible for maintaining the confidentiality of their login credentials and for all activities conducted under their account. Users must:</Typography>
                        <Box component="ul" sx={{ mb: 3 }}>
                            <li>Protect their username and password from unauthorized use</li>
                            <li>Immediately report any suspected unauthorized access or security concerns</li>
                            <li>Use the system only for its intended and authorized purposes</li>
                            <li>Ensure that any data entered into the system is accurate and appropriate</li>
                        </Box>
                        <Typography paragraph>Users are responsible for complying with all applicable privacy and data protection laws when accessing, viewing, or handling personal information contained within the system.</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2, mb: 0.5 }}>Prohibited Activities</Typography>
                        <Typography paragraph>Users may not engage in any activity that interferes with the integrity, security, or availability of the system. Prohibited activities include, but are not limited to:</Typography>
                        <Box component="ul" sx={{ mb: 3 }}>
                            <li>Attempting to gain unauthorized access to the system or other user accounts</li>
                            <li>Circumventing system security controls or attempting to exploit system vulnerabilities</li>
                            <li>Introducing malicious software, scripts, or other harmful code</li>
                            <li>Attempting to reverse engineer, probe, scan, or test the system for vulnerabilities</li>
                            <li>Excessive automated access, scraping, or data harvesting</li>
                            <li>Unauthorized copying, downloading, or distribution of system data</li>
                            <li>Using the system to transmit spam, fraudulent messages, or unethical marketing communications</li>
                            <li>Harassing, threatening, or abusing other users of the system</li>
                        </Box>
                        <Typography paragraph>Any attempt to disrupt system performance, damage system infrastructure, or interfere with the normal operation of the platform is strictly prohibited.</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2, mb: 0.5 }}>Monitoring and System Security</Typography>
                        <Typography paragraph>The system may be monitored for security, operational, and compliance purposes. By using this system, users acknowledge that activity may be logged, reviewed, and audited to ensure proper use and system protection.</Typography>
                        <Typography paragraph>Unauthorized use of the system may be investigated and may result in disciplinary action by the Licensee.</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2, mb: 0.5 }}>Data Protection</Typography>
                        <Typography paragraph>Users must handle all personal, financial, and educational information within the system in accordance with applicable privacy laws and the policies of the Licensee. Access to such information must be limited to authorized purposes only.</Typography>
                        <Typography paragraph>Users may not disclose, export, copy, or distribute personal data obtained from the system unless authorized to do so by the Licensee.</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2, mb: 0.5 }}>Password Security</Typography>
                        <Typography paragraph>Users are responsible for maintaining the confidentiality and security of their login credentials. Usernames and passwords must not be shared, transferred, or disclosed to any other individual.</Typography>
                        <Typography paragraph>Users must take reasonable precautions to protect their credentials, including using strong passwords and logging out of the system when access is complete. If a user believes their account credentials have been compromised or accessed without authorization, the user must immediately notify the Licensee&apos;s system administrator.</Typography>
                        <Typography paragraph>Users are responsible for all activity conducted through their account unless unauthorized access has been properly reported. Failure to maintain proper password security may result in suspension or termination of system access.</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2, mb: 0.5 }}>Consequences of Misuse</Typography>
                        <Typography paragraph>Violation of this agreement may result in one or more of the following actions:</Typography>
                        <Box component="ul" sx={{ mb: 3 }}>
                            <li>Suspension or termination of system access</li>
                            <li>Disciplinary action by the Licensee</li>
                            <li>Reporting of the activity to appropriate authorities</li>
                            <li>Civil or criminal penalties under applicable law</li>
                        </Box>
                        <Typography paragraph>Xenegrade and the Licensee reserve the right to immediately restrict or terminate access to the system if misuse, abuse, or security risks are identified.</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2, mb: 0.5 }}>System Ownership</Typography>
                        <Typography paragraph>The XenDirect software platform and related services are owned and operated by Xenegrade. Access to the system is provided to the Licensee under a separate service agreement. Users are granted access solely as authorized users of the Licensee.</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2, mb: 0.5 }}>Acceptance of Terms</Typography>
                        <Typography paragraph>By accessing or using this system, you acknowledge that you have read, understood, and agree to comply with this User Access and Acceptable Use Agreement.</Typography>
                    </DialogContent>
                    <Divider />
                    <DialogActions>
                        <Button onClick={() => setShowAgreement(false)} color="primary">Close</Button>
                    </DialogActions>
                </Dialog>

                <CardContent sx={{ bgcolor: "#fafafa" }}>
                    <Box sx={{ mx: "auto", maxWidth: "83%", textAlign: "right" }}>
                        <IconButton color="inherit" href="https://www.facebook.com/Xenegrade" size="large" sx={{ color: "#757575" }}>
                            <span className="mdi mdi-facebook-box" style={{ fontSize: 28 }} />
                        </IconButton>
                        <IconButton color="inherit" href="https://www.linkedin.com/in/rickstern" size="large" sx={{ color: "#757575" }}>
                            <span className="mdi mdi-linkedin-box" style={{ fontSize: 28 }} />
                        </IconButton>
                        <IconButton color="inherit" href="https://twitter.com/xenegrade" size="large" sx={{ color: "#757575" }}>
                            <span className="mdi mdi-twitter-box" style={{ fontSize: 28 }} />
                        </IconButton>
                        <IconButton color="inherit" href="http://blog.xenegrade.com/" size="large" sx={{ color: "#757575" }}>
                            <img
                                width="27"
                                height="27"
                                style={{ backgroundColor: "#757575", borderRadius: 4 }}
                                src="/images/xenegrade_logo_solid_white.png"
                                alt=""
                            />
                        </IconButton>
                    </Box>
                </CardContent>
            </Card>
        </form>
    );
}
